import { ReactNode } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { colors } from "../../../../theme/muiColors";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface GlobalSearchResultSectionProps<T> {
  title: string;
  results: T[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  error: unknown;
  onFetchNextPage: () => void;
  renderResult: (result: T) => ReactNode;
  getResultKey: (result: T) => string;
}

export function GlobalSearchResultSection<T>({
  title,
  results,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  error,
  onFetchNextPage,
  renderResult,
  getResultKey,
}: GlobalSearchResultSectionProps<T>) {
  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {title}
      </Typography>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" py={3}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!!error && !isLoading && (
        <Box display="flex" alignItems="center" gap={1} py={2}>
          <ErrorOutlineIcon sx={{ fontSize: 20, color: "error.main" }} />
          <Typography color="error" variant="body2">
            Failed to search messages
          </Typography>
        </Box>
      )}

      {!isLoading && !error && results.length === 0 && (
        <Box py={2}>
          <Typography color={colors.orange[300]} variant="body2">
            No results
          </Typography>
        </Box>
      )}

      {!isLoading && !error && results.map((result) => <Box key={getResultKey(result)}>{renderResult(result)}</Box>)}

      {hasNextPage && (
        <Box display="flex" justifyContent="center" py={1}>
          <Button size="small" onClick={onFetchNextPage} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? <CircularProgress size={16} /> : "Load more"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
